import type { ReactNode } from 'react';
import { Building2, FileText, MapPin, Network, ShieldCheck, UserCog } from 'lucide-react';
import LocationMiniMap, { type MapPoint } from '../create/components/LocationMiniMap';
import { PmoCard } from './ui/PmoCard';

/** Site rows parsed from the project's populated locations. */
export type SiteFact = {
  id: string;
  name: string;
  state?: string;
  lat?: number;
  lng?: number;
};

function Fact({ icon: Icon, label, value }: { icon: typeof MapPin; label: string; value: ReactNode }) {
  return (
    <div className="min-w-0 rounded-xl px-2.5 py-2" style={{ background: 'rgba(11,59,46,0.03)', border: '1px solid rgba(11,59,46,0.08)' }}>
      <dt className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] text-[#7C8A82]">
        <Icon className="h-3 w-3 shrink-0" strokeWidth={2.2} />
        {label}
      </dt>
      <dd className="mt-0.5 truncate text-[12.5px] font-extrabold text-[#0B3B2E]" title={typeof value === 'string' ? value : undefined}>
        {value}
      </dd>
    </div>
  );
}

export function ProjectFactsCard({
  code,
  name,
  customer,
  projectHead,
  projectManager,
  sites,
  fileCount,
  departmentCount,
}: {
  code: string;
  name: string;
  customer: string;
  projectHead: string;
  projectManager: string;
  sites: SiteFact[];
  fileCount: number;
  departmentCount: number;
}) {
  const points: MapPoint[] = sites
    .filter((s): s is SiteFact & { lat: number; lng: number } => s.lat != null && s.lng != null)
    .map((s, i) => ({ id: s.id, name: s.name, lat: s.lat, lng: s.lng, primary: i === 0 }));
  const primarySite = sites[0];
  const sitesLabel = sites.length
    ? sites.map((s) => s.name).join(', ')
    : '—';

  return (
    <PmoCard>
      {/* Identity strip — code chip + name */}
      <div className="px-4 pb-3 pt-3.5">
        <div className="flex items-center gap-2">
          <span
            className="shrink-0 rounded-md px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.1em]"
            style={{ background: 'rgba(201,169,97,0.16)', color: '#8A5A08', border: '1px solid rgba(201,169,97,0.45)' }}
          >
            {code}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#7C8A82]">Project facts</span>
        </div>
        <h4 className="mt-1.5 text-[14px] font-extrabold leading-snug tracking-tight text-[#0B3B2E] line-clamp-2">{name}</h4>
      </div>

      {/* Site map — same engine as the create wizard's Location tab, inset plate */}
      <div className="relative px-3">
        {points.length > 0 ? (
          <>
            <LocationMiniMap points={points} height={150} />
            <span
              className="pointer-events-none absolute right-5 top-2 z-[1001] rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em]"
              style={{ background: 'rgba(250,247,238,0.94)', color: '#1F5A47', border: '1px solid rgba(11,59,46,0.25)' }}
            >
              {sites.length} site{sites.length === 1 ? '' : 's'}
            </span>
            {primarySite ? (
              <span
                className="pointer-events-none absolute bottom-2 left-5 z-[1001] inline-flex max-w-[80%] items-center gap-1 rounded-md px-1.5 py-0.5 text-[9.5px] font-bold"
                style={{ background: 'rgba(250,247,238,0.94)', color: '#0B3B2E', border: '1px solid rgba(11,59,46,0.25)' }}
              >
                <span style={{ color: '#8A5A08' }}>★</span>
                <span className="truncate">
                  {primarySite.name}
                  {primarySite.state ? <span className="font-medium text-[#7C8A82]"> · {primarySite.state}</span> : null}
                </span>
              </span>
            ) : null}
          </>
        ) : (
          <div
            className="flex h-[76px] flex-col items-center justify-center gap-1 rounded-2xl text-center"
            style={{ border: '1px dashed rgba(11,59,46,0.22)', background: 'rgba(11,59,46,0.02)' }}
          >
            <MapPin className="h-4 w-4 text-[#7C8A82]" />
            <p className="text-[11px] font-medium text-[#7C8A82]">
              {sites.length ? 'Sites have no coordinates yet' : 'No site locations linked'}
            </p>
          </div>
        )}
      </div>

      {/* Compact fact grid */}
      <dl className="grid grid-cols-2 gap-2 px-3 py-3">
        <Fact icon={Building2} label="Customer" value={customer} />
        <Fact icon={ShieldCheck} label="Project head" value={projectHead} />
        <Fact icon={UserCog} label="Project manager" value={projectManager} />
        <Fact icon={MapPin} label={`Site${sites.length === 1 ? '' : 's'}`} value={sitesLabel} />
        <Fact icon={FileText} label="Documents" value={`${fileCount} file${fileCount === 1 ? '' : 's'}`} />
        <Fact icon={Network} label="Departments" value={String(departmentCount)} />
      </dl>
    </PmoCard>
  );
}
